/**
 * Destaques do portal → material para a peça social.
 *
 * O carrossel e o card nasceram pedindo que o operador COLASSE título, texto e
 * imagem da matéria. Isso é retrabalho: a matéria já está no WordPress do
 * tenant. Aqui fica a parte sem React: transformar o HTML do post no texto que
 * os validadores da copy usam como régua, e decidir quais categorias do site
 * contam como "IA e tech" sem chumbar id de categoria (id é por site).
 */
import type { ArticleDetail, ArticleSummary, CategoryInfo } from '@/types/article';

/** Texto limpo da matéria: o que a copy pode citar. Número fora daqui não entra. */
export function textoDaMateria(html: string | null | undefined, max = 40_000): string {
  if (!html) return '';
  // Fim de parágrafo vira espaço: sem isso "…parâmetros.</p><p>A Meta…" cola
  // as frases e a copy lê "parâmetros.A Meta".
  const comEspaco = html.replace(/<\/(p|li|h[1-6]|div|blockquote|tr)>|<br\s*\/?>/gi, ' $&');
  const doc = new DOMParser().parseFromString(comEspaco, 'text/html');
  // Legenda de foto, script e estilo não são fato da matéria.
  doc.querySelectorAll('script, style, figcaption, noscript').forEach((n) => n.remove());
  const texto = (doc.body.textContent ?? '').replace(/\s+/g, ' ').trim();
  return texto.length > max ? texto.slice(0, max) : texto;
}

/** Slugs de categoria que a MIRAI trata como IA e tech. Casamento por slug,
 * nunca por id: o id muda de site para site, o slug é a convenção editorial. */
const SLUGS_IA_TECH =
  /(^|-)(ia|ai|aidevops|intelig\w*|tech|tecnolog\w*|ciber\w*|devops|gestao-de-ti|lancamentos-de-ia)($|-)/i;

export function categoriasIaTech(categorias: CategoryInfo[]): CategoryInfo[] {
  return categorias.filter((c) => c.count > 0 && SLUGS_IA_TECH.test(c.slug));
}

export interface MaterialDaPeca {
  postId: number;
  titulo: string;
  texto: string;
  link: string;
  imageUrl: string;
  chapeu: string | null;
}

/** O que a peça precisa, a partir do post do WordPress. `categorias` serve só
 * para o chapéu (a faixa curta acima da headline). */
export function materialDoPost(post: ArticleDetail, categorias: CategoryInfo[]): MaterialDaPeca {
  const cat = categorias.find((c) => post.categories.includes(c.id));
  return {
    postId: post.id,
    titulo: decodificarEntidades(post.title),
    texto: textoDaMateria(post.content_html),
    link: post.link,
    imageUrl: post.featured_media?.source_url ?? '',
    chapeu: cat ? cat.name.slice(0, 40) : null,
  };
}

/** Títulos do WP REST vêm com `&amp;`, `&#8211;` etc. */
export function decodificarEntidades(s: string): string {
  const doc = new DOMParser().parseFromString(s, 'text/html');
  return (doc.body.textContent ?? '').trim();
}

export function resumoDoDestaque(a: ArticleSummary): string {
  return decodificarEntidades(a.title);
}
